export interface FeatureRecord {
  slug: string;
  name: string;
  shortDescription: string;
  fullDescription: string;
  whoItHelps: string[];
  howItWorksSteps: {
    title: string;
    description: string;
  }[];
  tierAvailability: {
    freeTier: string;
    proTier: string;
  };
  keyBenefits: string[];
  relatedGuideSlugs: string[];
  relatedIndustrySlugs: string[];
}

export const FEATURE_REGISTRY: Record<string, FeatureRecord> = {
  "local-business-discovery": {
    slug: "local-business-discovery",
    name: "Local Business Discovery Engine",
    shortDescription: "Search and map independent employers across 100+ categories within your local commute radius.",
    fullDescription: "BizNearYou's Discovery Engine helps job seekers, career switchers, and students find real, physically operating commercial businesses in their immediate geographic area, categorized by industry, headcount range, and service focus.",
    whoItHelps: [
      "Job seekers exploring local unposted opportunities near home",
      "Students looking for nearby clinics, trade contractors, or specialty shops",
      "Career switchers researching independent employers in specific zip codes"
    ],
    howItWorksSteps: [
      { title: "1. Enter Location & Radius", description: "Select your city or enter a custom zip code and set your target commute radius (1 to 25 miles)." },
      { title: "2. Filter by Category & Type", description: "Narrow results by specific industries like healthcare, trade services, law practices, or specialty retail." },
      { title: "3. Inspect Employer Profiles", description: "View verified addresses, websites, phone numbers, and operational activity indicators." }
    ],
    tierAvailability: {
      freeTier: "Unlimited local business searches, basic profile views, and mapped listings.",
      proTier: "Advanced category filtering, export capabilities, and bulk list management."
    },
    keyBenefits: [
      "Find independent employers missed by generic job boards",
      "Filter by precise geographic commute boundaries",
      "Access verified physical operational addresses"
    ],
    relatedGuideSlugs: ["how-to-find-small-business-jobs", "how-to-organize-a-local-job-search"],
    relatedIndustrySlugs: ["healthcare", "construction", "office-administration"]
  },
  "hiring-signals": {
    slug: "hiring-signals",
    name: "Hiring Signals Intelligence",
    shortDescription: "Identify businesses displaying commercial growth indicators like review spikes, expansion, and service updates.",
    fullDescription: "Hiring Signals evaluate public business growth metrics to detect companies experiencing increased workload or operational expansion before they formally post job advertisements.",
    whoItHelps: [
      "Proactive applicants wanting to reach out before job postings go live",
      "Trade specialists identifying expanding contractors with new fleet vehicles",
      "Administrative job seekers targeting busy medical or legal offices"
    ],
    howItWorksSteps: [
      { title: "1. Aggregate Operational Data", description: "The engine gathers public review activity, website service updates, and physical location footprint data." },
      { title: "2. Score Growth Momentum", description: "Signals are rated as High, Moderate, or Steady based on recent operational expansion metrics." },
      { title: "3. Direct Proactive Outreach", description: "Prioritize outreach to businesses with High signals where staffing demand is peak." }
    ],
    tierAvailability: {
      freeTier: "Basic Hiring Signal indicators on select local employer profiles.",
      proTier: "Full Hiring Signal breakdown, history timeline, and instant growth alerts."
    },
    keyBenefits: [
      "Discover expanding local employers before competitors",
      "Focus outreach on businesses with active operational growth",
      "Understand what drives employer hiring needs"
    ],
    relatedGuideSlugs: ["how-to-use-hiring-signals", "how-to-find-hidden-jobs"],
    relatedIndustrySlugs: ["construction", "healthcare", "automotive"]
  },
  "opportunity-score": {
    slug: "opportunity-score",
    name: "Opportunity Score Rating",
    shortDescription: "Algorithmic composite rating assessing employer contactability, business stability, and growth trajectory.",
    fullDescription: "The Opportunity Score aggregates multiple employer metrics—including website quality, contact info completeness, customer review volume, and operational stability—into an actionable 1-100 rating.",
    whoItHelps: [
      "Job seekers looking to prioritize high-yield outreach targets",
      "First-time applicants wanting to avoid dead-end contacts",
      "Organized job seekers structuring daily outreach lists"
    ],
    howItWorksSteps: [
      { title: "1. Evaluate Key Data Points", description: "Checks phone validity, website status, review rating consistency, and contact details." },
      { title: "2. Calculate Composite Score", description: "Generates a clear 1 to 100 score indicating employer responsiveness potential." },
      { title: "3. Sort & Prioritize", description: "Sort your target list by Opportunity Score to maximize outreach efficiency." }
    ],
    tierAvailability: {
      freeTier: "Standard Opportunity Scores on all searched employer profiles.",
      proTier: "Deep component breakdown and custom weighting options."
    },
    keyBenefits: [
      "Focus effort on highly responsive local businesses",
      "Avoid inactive or hard-to-contact businesses",
      "Structure daily outreach by highest conversion likelihood"
    ],
    relatedGuideSlugs: ["how-to-research-a-company-before-applying", "how-to-organize-a-local-job-search"],
    relatedIndustrySlugs: ["office-administration", "technology", "manufacturing"]
  },
  "saved-businesses": {
    slug: "saved-businesses",
    name: "Saved Businesses & Lists",
    shortDescription: "Bookmark, organize, and tag target employers into custom lists for systematic job hunting.",
    fullDescription: "Saved Businesses allows job seekers to create custom collections of local target employers, add private notes, tag priority targets, and organize their job search workflow.",
    whoItHelps: [
      "Methodical job seekers building target lists across multiple cities",
      "Students categorizing potential summer internship targets",
      "Trade workers organizing local contractor contact lists"
    ],
    howItWorksSteps: [
      { title: "1. Bookmark Target Employers", description: "Click the bookmark icon on any business card to save it instantly." },
      { title: "2. Organize into Folders", description: "Group businesses into custom folders by industry, location, or priority." },
      { title: "3. Add Personal Notes", description: "Keep track of key decision maker names, phone call outcomes, and follow-up dates." }
    ],
    tierAvailability: {
      freeTier: "Save up to 15 businesses with standard bookmarking.",
      proTier: "Unlimited saved lists, tags, custom notes, and CSV export."
    },
    keyBenefits: [
      "Keep all target local businesses organized in one workspace",
      "Track custom notes and contact history per employer",
      "Never lose track of promising local companies"
    ],
    relatedGuideSlugs: ["how-to-organize-a-local-job-search"],
    relatedIndustrySlugs: ["healthcare", "construction", "retail"]
  },
  "contact-search": {
    slug: "contact-search",
    name: "Key Contact & Decision Maker Search",
    shortDescription: "Find official business phone numbers, contact emails, and managing partner names.",
    fullDescription: "Contact Search aggregates publicly available phone numbers, official business contact emails, website contact forms, and key executive names so job seekers can reach decision makers directly.",
    whoItHelps: [
      "Applicants looking to address emails to specific managing partners or practice managers",
      "Trade workers calling shop superintendents directly",
      "Administrative applicants seeking office administrator contact information"
    ],
    howItWorksSteps: [
      { title: "1. Select Target Employer", description: "Open any verified business profile on BizNearYou." },
      { title: "2. Retrieve Verified Contacts", description: "View verified phone numbers, official domain emails, and contact form links." },
      { title: "3. Launch Direct Outreach", description: "Use built-in phone and email buttons to initiate professional contact." }
    ],
    tierAvailability: {
      freeTier: "Standard phone numbers and primary contact email/form links.",
      proTier: "Expanded contact discovery, direct owner names, and verified email lookups."
    },
    keyBenefits: [
      "Bypass generic info@ or hr@ dead ends",
      "Address emails directly to decision makers",
      "Save hours searching company websites manually"
    ],
    relatedGuideSlugs: ["how-to-email-a-business-about-a-job", "how-to-contact-a-local-employer"],
    relatedIndustrySlugs: ["office-administration", "technology", "healthcare"]
  },
  "website-review": {
    slug: "website-review",
    name: "Employer Website Review & Audit",
    shortDescription: "Analyze company websites for service details, career pages, and modern digital presence.",
    fullDescription: "Website Review evaluates local business websites to extract core services, staff bios, career mentions, and tech stack details, giving applicants deep context before reaching out.",
    whoItHelps: [
      "Applicants conducting pre-outreach research to customize pitch emails",
      "Tech job seekers evaluating company digital maturity",
      "Students looking for specific services to mention in cover letters"
    ],
    howItWorksSteps: [
      { title: "1. Instant Site Scan", description: "BizNearYou scans the employer's official website URL for operational details." },
      { title: "2. Extract Key Context", description: "Summarizes core services, team bios, and technology tools used." },
      { title: "3. Craft Tailored Pitch", description: "Use extracted insights to write highly specific pitch messages." }
    ],
    tierAvailability: {
      freeTier: "Basic website status and service summary views.",
      proTier: "Deep website tech audit, career link discovery, and technical summary context."
    },
    keyBenefits: [
      "Understand employer services in seconds",
      "Find hidden career or staff pages quickly",
      "Write customized outreach emails that stand out"
    ],
    relatedGuideSlugs: ["how-to-research-a-company-before-applying", "job-search-scam-warning-signs"],
    relatedIndustrySlugs: ["office-administration", "technology"]
  },
  "crm-pipeline": {
    slug: "crm-pipeline",
    name: "Job Search CRM & Pipeline Tracker",
    shortDescription: "Track job applications and direct inquiries from initial outreach to interview and hire.",
    fullDescription: "The CRM Pipeline is a visual Kanban board built for job seekers. Move target employers through customizable stages (Saved, Contacted, Followed Up, Interview Scheduled, Offer Extended) to maintain complete control of your local job search.",
    whoItHelps: [
      "Methodical applicants managing 20+ direct employer inquiries",
      "Career switchers balancing multiple introductory calls",
      "Anyone wanting to stay accountable with follow-up dates"
    ],
    howItWorksSteps: [
      { title: "1. Add Employers to Pipeline", description: "Add saved local businesses directly to your Kanban pipeline board." },
      { title: "2. Drag & Drop Status Stages", description: "Move employer cards from 'Contacted' to 'Followed Up' as you take action." },
      { title: "3. Track Reminders & History", description: "Set follow-up reminders and record interview notes per company." }
    ],
    tierAvailability: {
      freeTier: "Standard Kanban pipeline board with basic stage tracking.",
      proTier: "Advanced pipeline analytics, follow-up automation reminders, and CSV exports."
    },
    keyBenefits: [
      "Visual Kanban interface keeps your job search structured",
      "Never forget to send a follow-up email",
      "Track response rates and outreach conversion metrics"
    ],
    relatedGuideSlugs: ["how-to-organize-a-local-job-search", "how-to-follow-up-after-a-job-inquiry"],
    relatedIndustrySlugs: ["office-administration", "technology", "healthcare"]
  },
  "route-planner": {
    slug: "route-planner",
    name: "Local Business Visit Route Planner",
    shortDescription: "Plan efficient physical walk-in routes to visit multiple local businesses in one trip.",
    fullDescription: "The Route Planner optimizes multi-stop driving or walking routes for job seekers visiting local businesses in person, calculating travel times and clustering employers in commercial districts.",
    whoItHelps: [
      "Trade workers visiting shop managers in local industrial parks",
      "Retail applicants dropping off resumes at local shopping centers",
      "Anyone wanting to maximize in-person visit efficiency"
    ],
    howItWorksSteps: [
      { title: "1. Select Target Employers", description: "Pick 3 to 10 saved local businesses in your city." },
      { title: "2. Generate Optimized Route", description: "The engine maps the shortest travel sequence and estimates stop times." },
      { title: "3. Launch Map Directions", description: "Export the route to Google Maps or Apple Maps for seamless navigation." }
    ],
    tierAvailability: {
      freeTier: "Plan routes with up to 5 stops per trip.",
      proTier: "Unlimited route stops, custom duration estimates, and turn-by-turn exports."
    },
    keyBenefits: [
      "Save time and fuel when dropping off resumes in person",
      "Group employer visits logically by commercial district",
      "Map out efficient daily visit runs"
    ],
    relatedGuideSlugs: ["walk-in-job-application-tips"],
    relatedIndustrySlugs: ["construction", "retail", "automotive", "restaurants"]
  },
  "opportunity-radar": {
    slug: "opportunity-radar",
    name: "Opportunity Radar & Live Map Search",
    shortDescription: "Interactive geospatial radar mapping verified employers displaying recent growth activity.",
    fullDescription: "Opportunity Radar visualizes local commercial activity on an interactive map overlay, clustering verified employers by growth momentum, hiring likelihood, and industry category within your commute boundaries.",
    whoItHelps: [
      "Visual job seekers mapping employers near their neighborhood or public transit line",
      "Trade professionals exploring commercial corridors and industrial parks",
      "Healthcare workers finding clinics within a 15-minute drive"
    ],
    howItWorksSteps: [
      { title: "1. Set Center Point & Radius", description: "Drop a pin or enter your address to define your search radius." },
      { title: "2. View Color-Coded Pins", description: "Employers are color-coded by Opportunity Score and active Hiring Signals." },
      { title: "3. Click Pin to Inspect", description: "Preview address, contact options, and growth indicators with one tap." }
    ],
    tierAvailability: {
      freeTier: "Interactive radar map with standard business pins.",
      proTier: "Custom map layer controls, cluster filtering, and route sequencing."
    },
    keyBenefits: [
      "Visualize employer density around your exact home address",
      "Discover hidden commercial clusters missed by text search",
      "Filter map pins by active hiring momentum"
    ],
    relatedGuideSlugs: ["how-to-organize-a-local-job-search"],
    relatedIndustrySlugs: ["healthcare", "construction", "technology"]
  },
  "advanced-filters": {
    slug: "advanced-filters",
    name: "Advanced Employer & Commute Filters",
    shortDescription: "Filter local employers by headcount, website status, phone availability, review volume, and distance.",
    fullDescription: "Advanced Filters allow job seekers to refine their employer search beyond basic industry keywords. Filter by physical office status, direct contact availability, review growth, and exact mile radius.",
    whoItHelps: [
      "Applicants seeking small private practices vs. medium-sized shops",
      "Job seekers who prefer phone contact over web form inquiries",
      "Candidates looking specifically for businesses with verified websites"
    ],
    howItWorksSteps: [
      { title: "1. Open Filter Console", description: "Access detailed filter controls on any search or location directory page." },
      { title: "2. Toggle Operational Criteria", description: "Filter by phone availability, review counts, headcount estimate, or rating." },
      { title: "3. Apply & Save View", description: "Instantly refresh search results to view perfectly matching employers." }
    ],
    tierAvailability: {
      freeTier: "Standard industry, city, and radius filtering.",
      proTier: "Full operational criteria filters, custom saved filter views, and export filters."
    },
    keyBenefits: [
      "Save time by filtering out hard-to-reach or inactive businesses",
      "Target exact business size ranges (5-20 vs 20-50 employees)",
      "Focus exclusively on employers matching your preferred contact method"
    ],
    relatedGuideSlugs: ["how-to-find-small-business-jobs"],
    relatedIndustrySlugs: ["office-administration", "retail", "healthcare"]
  },
  "travel-time-search": {
    slug: "travel-time-search",
    name: "Travel Time & Radius Search",
    shortDescription: "Search for local employers based on realistic driving, transit, or walking commute durations.",
    fullDescription: "Travel Time Search helps candidates find employers within a realistic commute boundary—whether you are driving 15 minutes, walking 10 blocks, or riding public transit.",
    whoItHelps: [
      "Candidates prioritizing short, low-stress daily commutes",
      "Students relying on walking, cycling, or public transit",
      "Trade workers mapping employers along primary highway corridors"
    ],
    howItWorksSteps: [
      { title: "1. Choose Travel Mode", description: "Select Driving, Public Transit, Cycling, or Walking." },
      { title: "2. Set Max Duration", description: "Choose your maximum acceptable commute duration (e.g. 15 or 30 minutes)." },
      { title: "3. View Matching Employers", description: "Discover all verified local businesses inside your travel boundary." }
    ],
    tierAvailability: {
      freeTier: "Standard mile radius search boundaries.",
      proTier: "Isochrone travel time boundary mapping and transit routing."
    },
    keyBenefits: [
      "Ensure target employers fit your daily transportation reality",
      "Avoid job offers with miserable 60+ minute commutes",
      "Discover hidden employers closer to home than you realized"
    ],
    relatedGuideSlugs: ["how-to-organize-a-local-job-search"],
    relatedIndustrySlugs: ["construction", "healthcare", "retail", "automotive"]
  },
  "watchlists": {
    slug: "watchlists",
    name: "Employer Watchlists & Growth Alerts",
    shortDescription: "Monitor key local businesses and receive alerts when their growth signals or review activity spikes.",
    fullDescription: "Employer Watchlists allow candidates to track specific high-value local businesses over time. Get notified when an employer expands their facility, updates their web services, or shows surging customer review activity.",
    whoItHelps: [
      "Candidates waiting for the right moment to reach out to dream local employers",
      "Career switchers monitoring expanding local agencies and clinics",
      "Students tracking local firms for upcoming semester openings"
    ],
    howItWorksSteps: [
      { title: "1. Add Business to Watchlist", description: "Click 'Watch' on any local business profile." },
      { title: "2. System Tracks Signals", description: "BizNearYou monitors public operational updates, web changes, and review momentum." },
      { title: "3. Receive Growth Alerts", description: "Get notified when a watched business shows new commercial expansion signals." }
    ],
    tierAvailability: {
      freeTier: "Watch up to 3 local business profiles.",
      proTier: "Unlimited watchlists, instant growth alert notifications, and signal history logs."
    },
    keyBenefits: [
      "Time your outreach perfectly when an employer is actively expanding",
      "Keep tabs on top local target companies effortlessly",
      "Be the first candidate in line when growth happens"
    ],
    relatedGuideSlugs: ["how-to-use-hiring-signals"],
    relatedIndustrySlugs: ["technology", "healthcare", "construction"]
  },
  "business-comparison": {
    slug: "business-comparison",
    name: "Business & Employer Comparison Matrix",
    shortDescription: "Compare local employers side-by-side on rating, review trends, digital presence, and contactability.",
    fullDescription: "Business Comparison allows job seekers to evaluate two or more target employers side-by-side to compare Opportunity Scores, customer review velocity, website status, and contact detail completeness before initiating outreach.",
    whoItHelps: [
      "Applicants deciding which 5 local companies to pitch first this week",
      "Candidates weighing job opportunities across multiple local clinics or shops",
      "Methodical job seekers ranking employer stability and contactability"
    ],
    howItWorksSteps: [
      { title: "1. Select Companies to Compare", description: "Check 2 to 4 local businesses from your search results or saved list." },
      { title: "2. Open Comparison Matrix", description: "View a clear side-by-side table of key metrics and scores." },
      { title: "3. Choose Priority Target", description: "Identify which employer offers the highest response likelihood and stability." }
    ],
    tierAvailability: {
      freeTier: "Side-by-side comparison of up to 2 businesses.",
      proTier: "Multi-employer comparison matrices, score deep-dives, and exportable reports."
    },
    keyBenefits: [
      "Make data-driven decisions on where to invest outreach effort",
      "Compare business stability and customer review trends objectively",
      "Prioritize employers with the highest contactability ratings"
    ],
    relatedGuideSlugs: ["how-to-research-a-company-before-applying"],
    relatedIndustrySlugs: ["healthcare", "office-administration", "construction"]
  }
};

export function getAllFeatures(): FeatureRecord[] {
  return Object.values(FEATURE_REGISTRY);
}

export function getFeatureBySlug(slug: string): FeatureRecord | null {
  if (!slug) return null;
  const cleanSlug = slug.trim().toLowerCase();
  return FEATURE_REGISTRY[cleanSlug] || null;
}
